import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, canRole } from "@/lib/auth";
import { LogIn, LogOut, MapPin, Loader2, Plane, UserX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

async function getPosition() {
  if (!("geolocation" in navigator)) throw new Error("GPS not supported");
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

function ApplyLeaveDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ from_date: "", to_date: "", leave_type: "casual", reason: "" });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/leave-requests", f)).data,
    onSuccess: () => { toast.success("Leave applied"); qc.invalidateQueries({ queryKey: ["leaves"] }); setOpen(false); setF({ from_date: "", to_date: "", leave_type: "casual", reason: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="border border-[#0052FF] text-[#0052FF] hover:bg-[#EBF2FF] text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="apply-leave-btn">
          <Plane size={14} /> Apply Leave
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (!f.from_date || !f.to_date || !f.reason) { toast.error("All fields required"); return; } mut.mutate(); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From *</Label><Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="leave-from" /></div>
            <div><Label>To *</Label><Input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="leave-to" /></div>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={f.leave_type} onValueChange={(v) => setF({ ...f, leave_type: v })}>
              <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="sick">Sick</SelectItem>
                <SelectItem value="earned">Earned</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Reason *</Label><Textarea rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="leave-reason" /></div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full" data-testid="leave-submit">
            {mut.isPending ? "Submitting…" : "Submit Leave Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResignationDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/employees/${user.id}/resign`, { resignation_feedback: feedback })).data,
    onSuccess: () => { toast.success("Resignation submitted"); setOpen(false); setTimeout(() => window.location.href = "/admin/login", 1500); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="border border-red-500 text-red-700 hover:bg-red-50 text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="attendance-resign-btn">
          <UserX size={14} /> Submit Resignation
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>Submit Resignation</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (feedback.length < 10) { toast.error("Feedback (min 10 chars) required"); return; } mut.mutate(); }} className="space-y-3">
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">This action will deactivate your account. You will be logged out and lose access.</div>
          <div>
            <Label>Resignation Feedback *</Label>
            <Textarea rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Reason for resignation & feedback about your experience…" data-testid="attendance-resign-feedback" />
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2.5 rounded-full" data-testid="attendance-resign-submit">
            {mut.isPending ? "Submitting…" : "Submit Resignation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Attendance() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState("");
  const { data: myLog = [] } = useQuery({ queryKey: ["att-me"], queryFn: async () => (await api.get("/attendance/me", { params: { days: 30 } })).data });
  const { data: teamLog = [] } = useQuery({
    queryKey: ["att-all"], enabled: canRole(user, "branch_manager"),
    queryFn: async () => (await api.get("/attendance", { params: { days: 15 } })).data,
  });
  const { data: leaves = [] } = useQuery({ queryKey: ["leaves"], queryFn: async () => (await api.get("/leave-requests")).data });

  const mark = useMutation({
    mutationFn: async (action) => {
      let pos = { lat: null, lng: null };
      try { pos = await getPosition(); } catch { toast.warning("GPS unavailable — marking without location"); }
      return (await api.post("/attendance/mark", { action, ...pos, remarks: remarks || null })).data;
    },
    onSuccess: () => { toast.success("Attendance marked"); qc.invalidateQueries({ queryKey: ["att-me"] }); qc.invalidateQueries({ queryKey: ["att-all"] }); setRemarks(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
    onSettled: () => setBusy(false),
  });

  const today = myLog.find((a) => a.date === new Date().toISOString().slice(0, 10));

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">GPS Attendance</h1>
          <p className="text-sm text-[#52525B]">Mark your daily check-in/check-out, apply for leave, or submit resignation.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ApplyLeaveDialog />
          <ResignationDialog />
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-[#E4E4E7] rounded-xl p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#52525B]">Today · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}</div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="border border-[#E4E4E7] rounded-xl p-4">
              <div className="text-xs text-[#52525B]">Check-in</div>
              <div className="font-mono-num text-xl font-bold text-[#06152D] mt-1">{today?.check_in ? new Date(today.check_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
              {today?.check_in_lat && <div className="mt-1 text-[10px] text-[#52525B] inline-flex items-center gap-1"><MapPin size={10} /> {today.check_in_lat.toFixed(4)}, {today.check_in_lng?.toFixed(4)}</div>}
            </div>
            <div className="border border-[#E4E4E7] rounded-xl p-4">
              <div className="text-xs text-[#52525B]">Check-out</div>
              <div className="font-mono-num text-xl font-bold text-[#06152D] mt-1">{today?.check_out ? new Date(today.check_out).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
              {today?.check_out_lat && <div className="mt-1 text-[10px] text-[#52525B] inline-flex items-center gap-1"><MapPin size={10} /> {today.check_out_lat.toFixed(4)}, {today.check_out_lng?.toFixed(4)}</div>}
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs">Remarks (optional)</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Field visit to Karimnagar" data-testid="attendance-remarks" />
          </div>
          <div className="mt-4 flex gap-3">
            <button
              disabled={busy || !!today?.check_in}
              onClick={() => { setBusy(true); mark.mutate("check_in"); }}
              className="flex-1 bg-[#0052FF] hover:bg-[#003FCC] disabled:opacity-60 text-white font-semibold px-4 py-3 rounded-full inline-flex items-center justify-center gap-2"
              data-testid="checkin-btn"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Check-in
            </button>
            <button
              disabled={busy || !today?.check_in || !!today?.check_out}
              onClick={() => { setBusy(true); mark.mutate("check_out"); }}
              className="flex-1 bg-[#06152D] hover:bg-[#0b2350] disabled:opacity-40 text-white font-semibold px-4 py-3 rounded-full inline-flex items-center justify-center gap-2"
              data-testid="checkout-btn"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />} Check-out
            </button>
          </div>
          <div className="mt-4 text-xs text-[#52525B]">Reminder at 6:45 PM · Auto check-out at 8:00 PM if forgotten.</div>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-xl p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#52525B]">Last 30 days</div>
          <div className="mt-3 text-sm text-[#09090b] space-y-1">
            <div>Present: <span className="font-mono-num font-bold">{myLog.filter(a => a.check_in).length}</span></div>
            <div>Auto check-out: <span className="font-mono-num font-bold">{myLog.filter(a => a.auto_checkout).length}</span></div>
            <div>Missed check-out: <span className="font-mono-num font-bold">{myLog.filter(a => a.check_in && !a.check_out).length}</span></div>
            <div>Leaves applied: <span className="font-mono-num font-bold">{leaves.length}</span></div>
          </div>
        </div>
      </div>

      {/* Leave history */}
      <h2 className="mt-8 font-display text-xl font-bold text-[#06152D]">My Leave Requests</h2>
      <div className="mt-3 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="my-leave-table">
          <thead><tr><th>Applied</th><th>From</th><th>To</th><th>Type</th><th>Status</th><th>Reason</th></tr></thead>
          <tbody>
            {leaves.length === 0 && <tr><td colSpan="6" className="text-center text-[#52525B] py-6">No leave requests.</td></tr>}
            {leaves.map((l) => (
              <tr key={l.id}>
                <td className="text-xs">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                <td className="text-xs">{l.from_date}</td>
                <td className="text-xs">{l.to_date}</td>
                <td className="text-xs">{l.leave_type}</td>
                <td><Badge variant="secondary" className={l.status === "approved" ? "bg-emerald-100 text-emerald-800" : l.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>{l.status}</Badge></td>
                <td className="text-xs">{l.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 font-display text-xl font-bold text-[#06152D]">My Attendance History</h2>
      <div className="mt-3 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="my-att-table">
          <thead><tr><th>Date</th><th>Check-in</th><th>Check-out</th><th>Location</th><th>Remarks</th></tr></thead>
          <tbody>
            {myLog.length === 0 && <tr><td colSpan="5" className="text-center text-[#52525B] py-6">No attendance yet.</td></tr>}
            {myLog.map((a) => (
              <tr key={a.id}>
                <td className="font-mono-num text-xs">{a.date}</td>
                <td className="font-mono-num text-xs">{a.check_in ? new Date(a.check_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                <td className="font-mono-num text-xs">{a.check_out ? new Date(a.check_out).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"} {a.auto_checkout && <Badge variant="secondary" className="ml-1 text-[9px]">auto</Badge>}</td>
                <td className="text-xs text-[#52525B]">{a.check_in_lat ? `${a.check_in_lat.toFixed(3)}, ${a.check_in_lng?.toFixed(3)}` : "—"}</td>
                <td className="text-xs">{a.remarks || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canRole(user, "branch_manager") && (
        <>
          <h2 className="mt-8 font-display text-xl font-bold text-[#06152D]">Team attendance (last 15 days)</h2>
          <div className="mt-3 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="team-att-table">
              <thead><tr><th>Date</th><th>Employee</th><th>In</th><th>Out</th><th>Location</th></tr></thead>
              <tbody>
                {teamLog.length === 0 && <tr><td colSpan="5" className="text-center text-[#52525B] py-6">No team records.</td></tr>}
                {teamLog.map((a) => (
                  <tr key={a.id}>
                    <td className="font-mono-num text-xs">{a.date}</td>
                    <td>{a.user_name}</td>
                    <td className="font-mono-num text-xs">{a.check_in ? new Date(a.check_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="font-mono-num text-xs">{a.check_out ? new Date(a.check_out).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="text-xs text-[#52525B]">{a.check_in_lat ? `${a.check_in_lat.toFixed(3)}, ${a.check_in_lng?.toFixed(3)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
